/**
 * @author Radek Liška <ing.radek.liska@gmail.com>
 * @copyright Ki-Wi Digital s.r.o.
 * @license
 * All Rights Reserved.
 * Unauthorized copying of this file is strictly prohibited.
 * Proprietary and confidential.
 */

// @ts-check

import ErrorName from '../../../core/error/error-name';
import { errorWithName } from '../../../core/error/error-with-name';
import OrientationEnum from '../../../core/orientation/orientation-enum';

/**
 * @import { Fraction } from '../../../core/fraction'
 * @import IScreenManager from '../../../core/screen/screen-manager'
 */

// Konstanty pro mapování mezi podsvícením (<0,1>) a CSS jasem (<0.5,1>).
const backlightRange = { min: 0, max: 1 };
const cssBacklightRange = { min: 0.5, max: 1 };

/**
 * Správce obrazovky pro standardní webové technologie.
 * @implements {IScreenManager}
 */
export default class ScreenManager {
  /** @type {IScreenManager['createScreenshot']} */
  async createScreenshot() {
    throw await errorWithName(
      ErrorName.NotSupportedError,
      'Unable to create the screenshot.'
    );
  }

  /** @type {IScreenManager['getBacklight']} */
  async getBacklight() {
    const backlight = parseFloat(
      document.body.style.filter.replace('brightness(', '').replace(')', '')
    );
    if (isNaN(backlight)) {
      // Výchozí hodnota na webe je 1 (u nás maxBacklight).
      return await backlightRange.max;
    }
    return await mapLinear(backlight, cssBacklightRange, backlightRange);
  }

  /** @type {IScreenManager['getBrightness']} */
  async getBrightness() {
    throw await errorWithName(
      ErrorName.NotSupportedError,
      'Unable to get the brightness.'
    );
  }

  /** @type {IScreenManager['getCssOrientation']} */
  async getCssOrientation() {
    return await OrientationEnum.CLOCKWISE_0;
  }

  /** @type {IScreenManager['getOrientation']} */
  async getOrientation() {
    // Webová implementace se typicky používá pro náhledy,
    // kde je nežádoucí měnit rotaci.
    return await OrientationEnum.CLOCKWISE_0;
  }

  /** @type {IScreenManager['setBacklight']} */
  async setBacklight(value) {
    const backlight = mapLinear(value, backlightRange, cssBacklightRange);
    document.body.style.filter = `brightness(${backlight})`;
    return await Promise.resolve();
  }

  /** @type {IScreenManager['setOrientation']} */
  async setOrientation() {}
}

/**
 * Prepočet hodnoty z jedneho rozsahu do druhého.
 * @param {Fraction} value
 * @param {{min: Fraction, max: Fraction}} domain
 * @param {{min: Fraction, max: Fraction}} range
 * @returns {Fraction} Zaokrouhlené na 2 desetinná místa.
 */
function mapLinear(value, domain, range) {
  return parseFloat(
    (
      ((value - domain.min) * (range.max - range.min)) /
        (domain.max - domain.min) +
      range.min
    ).toFixed(2)
  );
}
